import { useCallback, useRef, useState } from "react";

const DEFAULT_MAX_HISTORY = 50;
const INSPECTOR_GROUP_MS = 500;

export function createWorkflowCanvasSnapshot({ nodes, edges, workflowStart }) {
    return {
        nodes: structuredClone(nodes),
        edges: structuredClone(edges),
        workflowStart: workflowStart ?? "",
    };
}

function snapshotsEqual(a, b) {
    if (!a || !b) return false;
    return (
        a.workflowStart === b.workflowStart
        && JSON.stringify(a.nodes) === JSON.stringify(b.nodes)
        && JSON.stringify(a.edges) === JSON.stringify(b.edges)
    );
}

/**
 * Undo/redo stack for workflow canvas state (nodes, edges, workflowStart).
 * Call recordBeforeChange() immediately before a user-driven mutation.
 */
export function useWorkflowCanvasHistory({ maxHistory = DEFAULT_MAX_HISTORY } = {}) {
    const pastRef = useRef([]);
    const futureRef = useRef([]);
    const isRestoringRef = useRef(false);
    const inspectorGroupActiveRef = useRef(false);
    const inspectorGroupTimerRef = useRef(null);
    const [revision, setRevision] = useState(0);

    const bump = useCallback(() => {
        setRevision((value) => value + 1);
    }, []);

    const resetHistory = useCallback(() => {
        pastRef.current = [];
        futureRef.current = [];
        inspectorGroupActiveRef.current = false;
        clearTimeout(inspectorGroupTimerRef.current);
        bump();
    }, [bump]);

    const recordBeforeChange = useCallback((snapshot) => {
        if (isRestoringRef.current || !snapshot) return;

        const past = pastRef.current;
        const last = past[past.length - 1];
        if (last && snapshotsEqual(last, snapshot)) return;

        pastRef.current = [...past.slice(-(maxHistory - 1)), snapshot];
        futureRef.current = [];
        bump();
    }, [maxHistory, bump]);

    const recordBeforeInspectorChange = useCallback((snapshot) => {
        if (isRestoringRef.current || !snapshot) return;

        if (!inspectorGroupActiveRef.current) {
            recordBeforeChange(snapshot);
            inspectorGroupActiveRef.current = true;
        }

        clearTimeout(inspectorGroupTimerRef.current);
        inspectorGroupTimerRef.current = setTimeout(() => {
            inspectorGroupActiveRef.current = false;
        }, INSPECTOR_GROUP_MS);
    }, [recordBeforeChange]);

    const undo = useCallback((currentSnapshot, applySnapshot) => {
        const past = pastRef.current;
        if (past.length === 0 || !currentSnapshot || !applySnapshot) return false;

        const previous = past[past.length - 1];
        isRestoringRef.current = true;
        pastRef.current = past.slice(0, -1);
        futureRef.current = [...futureRef.current, currentSnapshot];
        applySnapshot(previous);
        inspectorGroupActiveRef.current = false;
        clearTimeout(inspectorGroupTimerRef.current);
        bump();
        queueMicrotask(() => {
            isRestoringRef.current = false;
        });
        return true;
    }, [bump]);

    const redo = useCallback((currentSnapshot, applySnapshot) => {
        const future = futureRef.current;
        if (future.length === 0 || !currentSnapshot || !applySnapshot) return false;

        const next = future[future.length - 1];
        isRestoringRef.current = true;
        futureRef.current = future.slice(0, -1);
        pastRef.current = [...pastRef.current, currentSnapshot];
        applySnapshot(next);
        inspectorGroupActiveRef.current = false;
        clearTimeout(inspectorGroupTimerRef.current);
        bump();
        queueMicrotask(() => {
            isRestoringRef.current = false;
        });
        return true;
    }, [bump]);

    const canUndo = pastRef.current.length > 0;
    const canRedo = futureRef.current.length > 0;

    return {
        revision,
        canUndo,
        canRedo,
        isRestoringRef,
        resetHistory,
        recordBeforeChange,
        recordBeforeInspectorChange,
        undo,
        redo,
    };
}
